import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import DashboardLayout from '../../components/DashboardLayout';

import PendingStudentsList from './PendingStudentsList';
import CampusesList from './CampusesList';
import QuickActionsMenu from './QuickActionsMenu';
import StatisticsCards from './StatisticsCards';
import PendingEnrollmentsList from './PendingEnrollmentsList';

function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [profile, setProfile] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [pendingStudentsCount, setPendingStudentsCount] = useState(0);
  const [stats, setStats] = useState({
    approvedEnrollments: 0,
    pendingEnrollments: 0,
    completedEnrollments: 0,
    withdrawnEnrollments: 0,
    activeCohorts: 0,
  });
  const [pendingEnrollments, setPendingEnrollments] = useState([]);

  const [isRefetching, setIsRefetching] = useState(false);

  const isAdmin = useMemo(() => {
    return user?.is_staff || ['admin', 'campus_admin', 'finance', 'superuser'].includes(user?.user_type) || user?.is_superuser;
  }, [user]);

  const getResults = (res) => {
    if (!res) return [];
    const data = res.data ?? res;
    return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
  };

  const getCount = (res) => {
    if (!res) return 0;
    const data = res.data ?? res;
    if (typeof data.count === 'number') return data.count;
    const results = Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
    return results.length;
  };

  const fetchDashboardData = useCallback(async () => {
    setError(null);
    setIsRefetching(true);
    setLoading(true);
    try {
      const [
        profileRes,
        pendingStudentsListRes,
        pendingStudentsCountRes,
        campusesRes,
        approvedEnrollmentsRes,
        pendingEnrollmentsRes,
        completedEnrollmentsRes,
        withdrawnEnrollmentsRes,
        activeCohortsRes,
      ] = await Promise.all([
        api.get('/auth/users/profile/'),
        api.get('/auth/users/students/', { params: { status: 'pending', limit: 5 } }),
        api.get('/auth/users/students/', { params: { status: 'pending' } }),
        api.get('/campuses/campuses/', { params: { page_size: 50 } }),
        api.get('/enrollment/enrollments/', { params: { status: 'approved', page: 1, page_size: 1 } }),
        api.get('/enrollment/enrollments/', { params: { status: 'pending', page: 1, page_size: 5 } }),
        api.get('/enrollment/enrollments/', { params: { status: 'completed', page: 1, page_size: 1 } }),
        api.get('/enrollment/enrollments/', { params: { status: 'withdrawn', page: 1, page_size: 1 } }),
        api.get('/enrollment/cohorts/', { params: { is_active: true, page: 1, page_size: 1 } }),
      ]);

      const profileData = profileRes.data;
      const pendingStudentsResults = getResults(pendingStudentsListRes);
      const pendingStudentsTotal = getCount(pendingStudentsCountRes);
      const campusResults = getResults(campusesRes);
      const activeCampuses = campusResults.filter((c) => c?.is_active);

      setProfile(profileData);
      setPendingStudents(pendingStudentsResults);
      setPendingStudentsCount(pendingStudentsTotal);
      setCampuses(activeCampuses);

      setStats({
        approvedEnrollments: getCount(approvedEnrollmentsRes),
        pendingEnrollments: getCount(pendingEnrollmentsRes),
        completedEnrollments: getCount(completedEnrollmentsRes),
        withdrawnEnrollments: getCount(withdrawnEnrollmentsRes),
        activeCohorts: getCount(activeCohortsRes),
      });

      setPendingEnrollments(getResults(pendingEnrollmentsRes));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Failed to load admin dashboard.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchDashboardData();
  }, [fetchDashboardData, isAdmin]);

  const welcomeMessage = useMemo(() => {
    const campusName = profile?.campus_name;
    if (campusName) return `Welcome back to ${campusName}!`;
    const userName = profile?.first_name || profile?.username || 'Account';
    return `Welcome back, ${userName}!`;
  }, [profile]);

  const campusOptions = useMemo(() => {
    const items = profile?.user_campuses || [];
    return items
      .filter((x) => x && x.is_active)
      .map((x) => ({
        id: Number(x.campus ?? x.id),
        campus_name: x.campus_name,
      }));
  }, [profile]);

  const isOrgWideAdmin = user?.is_superuser || profile?.user_type === 'admin';

  const switchOptions = useMemo(() => {
    if (campusOptions.length > 0) return campusOptions;
    if (isOrgWideAdmin && campuses.length > 0) {
      return campuses.map((c) => ({ id: c.id, campus_name: c.name }));
    }
    return [];
  }, [campusOptions, isOrgWideAdmin, campuses]);

  const selectedCampusId = useMemo(() => {
    if (profile?.campus) return Number(profile.campus);
    const selected = profile?.user_campuses?.find((uc) => uc.is_selected);
    if (selected) return Number(selected.campus ?? selected.id);
    if (switchOptions.length === 1) return switchOptions[0].id;
    return null;
  }, [profile, switchOptions]);

  const canSwitchCampus = switchOptions.length > 1;

  const handleCampusChange = async (newCampusId) => {
    if (!profile?.id) return;
    try {
      await api.patch(`/auth/users/${profile.id}/`, { campus: newCampusId });
      toast.success('Campus updated');
      await fetchDashboardData();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Failed to update campus';
      toast.error(msg);
    }
  };

  const handleApproveStudent = async (studentId) => {
    await api.post(`/auth/users/${studentId}/approve/`, {});
    toast.success('Student approved');
    await fetchDashboardData();
  };

  const handleRejectStudent = async (studentId) => {
    await api.post(`/auth/users/${studentId}/reject/`, {});
    toast.success('Student rejected');
    await fetchDashboardData();
  };

  if (!isAdmin) {
    return <div style={{ padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>Access denied.</div>;
  }

  return (
    <DashboardLayout>
      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div
          style={{
            color: 'var(--color-text-secondary)',
            fontWeight: 800,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
          }}
        >
          Admin Overview
        </div>
        <div style={{ marginTop: 8, fontSize: '1.75rem', fontWeight: 900, lineHeight: 1.1 }}>
          <span style={{ color: 'var(--text-accent-cyan)' }}>{welcomeMessage}</span>
        </div>
      </div>

      <section className="dashboard-hero">
        <div className="dashboard-hero-content">
          <div className="dashboard-hero-kicker">AI-Powered Admin Workspace</div>
          <h2>Monitor enrollment flow with real-time decision support.</h2>
          <p>
            Keep approvals, cohorts, and course operations in one place while preserving the same
            workflow and actions your team already uses.
          </p>
        </div>
        <div className="dashboard-hero-visual" aria-hidden="true">
          <div className="ai-orb ai-orb-main" />
          <div className="ai-orb ai-orb-secondary" />
          <div className="ai-grid" />
        </div>
      </section>

      {!error && (
        <QuickActionsMenu />
      )}

      {loading ? (
        <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading dashboard...
        </div>
      ) : error ? (
        <div className="text-error" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
          <div style={{ fontWeight: 900, marginBottom: 12 }}>Could not load dashboard</div>
          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>{error}</div>
          <button className="btn btn-primary" onClick={fetchDashboardData} type="button">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
            <PendingStudentsList
              students={pendingStudents}
              count={pendingStudentsCount}
              isLoading={isRefetching}
              maxToShow={5}
              onApprove={handleApproveStudent}
              onReject={handleRejectStudent}
              viewAllTo="/admin/pending-students"
            />

            <CampusesList
              campuses={campuses}
              profile={profile}
              currentCampusId={selectedCampusId}
              canSwitchCampus={canSwitchCampus}
              campusOptions={switchOptions}
              isOrgWideAdmin={isOrgWideAdmin}
              onCampusChange={handleCampusChange}
              viewAllTo="/admin/campuses"
              isLoading={isRefetching}
            />
          </div>

          <div style={{ marginTop: 'var(--spacing-xl)' }}>
            <StatisticsCards stats={stats} />
          </div>

          <div style={{ marginTop: 'var(--spacing-xl)' }}>
            <PendingEnrollmentsList
              enrollments={pendingEnrollments}
              isLoading={isRefetching}
              viewAllTo="/admin/enrollments"
            />
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

export default AdminDashboard;
